import { makeAutoObservable } from 'mobx'
import type {
  AnalyticsChartPeriod,
  AnalyticsResponse,
  IConversationsChartResponse,
  IConversationsStatisticResponse,
  ProcessedStatistic,
} from 'entities/Analytics'
import { formatDateForDisplay, calculateTrend, computeHasStatistic } from 'pages/analytics/lib'

interface ConversationsRechartsDataPoint {
  date: string
  name: string
  open: number
  closed: number
  total: number
  new: number
}

export class ConversationsChartDataStore {
  private _rawStatistic: IConversationsStatisticResponse[] = []
  private _rawChartData: IConversationsChartResponse[] = []
  private _period: AnalyticsChartPeriod | null = null

  constructor() {
    makeAutoObservable(this)
  }

  setRawData = (
    response: AnalyticsResponse<IConversationsStatisticResponse, IConversationsChartResponse>,
    period?: AnalyticsChartPeriod
  ) => {
    this._rawStatistic = response.statistic
    this._rawChartData = response.charts
    if (period) {
      this._period = period
    }
  }

  get processedStatistic(): ProcessedStatistic[] {
    return this._rawStatistic.map((item) => {
      const { key, value, prevValue, label } = item
      const trend = calculateTrend(value, prevValue)

      return {
        key: key,
        label: label || key,
        value: value,
        prevValue: prevValue,
        trend,
      }
    })
  }

  get rechartsData(): ConversationsRechartsDataPoint[] {
    return this._rawChartData
      .map((item) => ({
        date: item.date,
        name: formatDateForDisplay(item.date, this._period ?? 'week'),
        open: item.open_conv || 0,
        closed: item.closed || 0,
        total: item.total || 0,
        new: item.new || 0,
      }))
      .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime())
  }

  getStatisticByKey = (key: string): ProcessedStatistic | undefined => {
    return this.processedStatistic.find((item) => item.key === key)
  }

  get hasStatistic() {
    return computeHasStatistic(this._rawStatistic)
  }

  dispose = () => {
    this._rawStatistic = []
    this._rawChartData = []
    this._period = null
  }
}
